import { useCallback, useEffect, useRef, useState } from 'react'
import type { FaceLandmarker, FaceLandmarkerResult } from '@mediapipe/tasks-vision'
import { Banner, Button, Loader, Meter, Text } from '@cloudflare/kumo'
import { CameraIcon, CheckCircleIcon, CircleIcon } from '@phosphor-icons/react'
import { useTranslation } from '../../../i18n'
import { StickyActionBar } from '../StickyActionBar'
import { encodePhoto } from './capture'
import {
  type FaceBox,
  type FrameChecks,
  type HintKey,
  evaluateFrame,
  faceBox,
  pixelStats,
} from './checks'
import { SELFIE_CONFIG as C } from './config'
import { loadLandmarker } from './landmarker'
import '../enroll.css'

interface GuidedSelfieProps {
  /** Receives the final cropped JPEG as a data URL. */
  onCapture: (dataUrl: string) => void
}

type CameraState = 'starting' | 'ready' | 'denied' | 'unavailable' | 'stopped'
type Engine = 'loading' | 'ready' | 'unavailable'

interface Ui {
  hint: HintKey | null
  checks: FrameChecks
  ok: boolean
  /** Share of the hold-still countdown done, 0..1 (5% steps). */
  progress: number
}

const EMPTY_UI: Ui = {
  hint: null,
  checks: {
    oneFace: false,
    closeEnough: false,
    centred: false,
    lightSharp: false,
  },
  ok: false,
  progress: 0,
}

const CHECKLIST: { key: keyof FrameChecks; label: string }[] = [
  { key: 'oneFace', label: 'enroll.selfie.check.oneFace' },
  { key: 'closeEnough', label: 'enroll.selfie.check.close' },
  { key: 'centred', label: 'enroll.selfie.check.centred' },
  { key: 'lightSharp', label: 'enroll.selfie.check.light' },
]

/**
 * Guided selfie capture: takes the photo once `minPassedChecks` of the 4
 * checks hold for `stableMs`. Owns the camera and the (lazily
 * loaded) MediaPipe landmarker; everything runs in the browser and only the
 * final JPEG is handed to `onCapture`. Falls back to manual capture when
 * the automatic checks cannot load.
 */
export function GuidedSelfie({ onCapture }: GuidedSelfieProps) {
  const { t } = useTranslation()
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const frameRef = useRef<HTMLDivElement | null>(null)
  const landmarkerRef = useRef<FaceLandmarker | null>(null)
  const boxRef = useRef<FaceBox | null>(null)
  const onCaptureRef = useRef(onCapture)
  onCaptureRef.current = onCapture
  // `t` may change identity on every render; the detection loop must not
  // restart because of it.
  const tRef = useRef(t)
  tRef.current = t

  const [attempt, setAttempt] = useState(0)
  const [camera, setCamera] = useState<CameraState>('starting')
  const [engine, setEngine] = useState<Engine>('loading')
  const [ui, setUi] = useState<Ui>(EMPTY_UI)
  const [notice, setNotice] = useState<string | null>(null)
  const [troubled, setTroubled] = useState(false)
  const [smallCamera, setSmallCamera] = useState(false)
  // Set when automatic detection is broken (errors / never finds a face): the
  // manual capture then skips the still-image face check so nobody is locked out.
  const skipDetectRef = useRef(false)
  const rejectsRef = useRef(0)

  // Camera.
  useEffect(() => {
    let cancelled = false
    let stream: MediaStream | null = null
    setCamera('starting')
    setNotice(null)
    setTroubled(false)
    setSmallCamera(false)
    const stopped = () => setCamera((c) => (c === 'ready' ? 'stopped' : c))
    const onVisible = () => {
      if (!document.hidden && stream?.getVideoTracks().some((tr) => tr.readyState === 'ended')) {
        stopped()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    const md = navigator.mediaDevices
    if (!md?.getUserMedia) {
      setCamera('unavailable')
      return
    }
    md.getUserMedia({ video: C.camera })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((tr) => tr.stop())
          return
        }
        stream = s
        s.getVideoTracks?.().forEach((tr) => tr.addEventListener?.('ended', stopped))
        const video = videoRef.current
        if (video) {
          video.srcObject = s
          void video.play?.().catch(() => undefined)
        }
        setCamera('ready')
        // The page header, details and progress sit above; bring the preview up.
        frameRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const name = (err as { name?: string })?.name
        setCamera(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable')
      })
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      stream?.getTracks().forEach((tr) => tr.stop())
    }
  }, [attempt])

  // MediaPipe (lazy chunk + self-hosted assets), with a timeout fallback.
  useEffect(() => {
    let cancelled = false
    let settled = false
    const timer = setTimeout(() => {
      if (!settled && !cancelled) setEngine('unavailable')
    }, C.loadTimeoutMs)
    loadLandmarker()
      .then((lm) => {
        landmarkerRef.current = lm
        settled = true
        clearTimeout(timer)
        if (cancelled) lm.close()
        else setEngine((e) => (e === 'unavailable' ? e : 'ready'))
      })
      .catch(() => {
        settled = true
        clearTimeout(timer)
        if (!cancelled) setEngine('unavailable')
      })
    return () => {
      cancelled = true
      clearTimeout(timer)
      landmarkerRef.current?.close()
      landmarkerRef.current = null
    }
  }, [])

  const finish = useCallback((face: FaceBox | null, source?: CanvasImageSource) => {
    const video = videoRef.current
    if (!video) return false
    const w = video.videoWidth
    const h = video.videoHeight
    const res = encodePhoto(source ?? video, w, h, face, true)
    if (!res.ok) {
      setNotice(tRef.current('enroll.selfie.error.encode'))
      return false
    }
    onCaptureRef.current(res.dataUrl)
    return true
  }, [])

  // Detection loop (VIDEO mode, throttled).
  useEffect(() => {
    if (camera !== 'ready' || engine !== 'ready') return
    const lm = landmarkerRef.current
    const video = videoRef.current
    if (!lm || !video) return
    let raf = 0
    let last = 0
    let stopped = false
    let okSince: number | null = null
    let badSince: number | null = null
    let lastKey = ''
    let sizeChecked = false
    let detectErrors = 0
    let lastFaceAt = performance.now()
    let hiddenAt: number | null = null
    let troubleLeft: number = C.troubleAfterMs
    let troubleStart = performance.now()
    let troubleTimer = setTimeout(() => setTroubled(true), troubleLeft)
    const giveUpOnAuto = () => {
      skipDetectRef.current = true
      stopped = true
      setEngine('unavailable')
    }
    // Pause every timer while the tab is hidden, so coming back is not a
    // failed attempt.
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = performance.now()
        clearTimeout(troubleTimer)
        troubleLeft -= hiddenAt - troubleStart
      } else if (hiddenAt !== null) {
        const delta = performance.now() - hiddenAt
        hiddenAt = null
        okSince = null
        badSince = null
        lastFaceAt += delta
        troubleStart = performance.now()
        troubleTimer = setTimeout(() => setTroubled(true), Math.max(0, troubleLeft))
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    const small = document.createElement('canvas')

    const tick = (time: number) => {
      if (stopped) return
      raf = requestAnimationFrame(tick)
      if (document.hidden || time - last < C.detectIntervalMs) return
      last = time
      if (video.readyState < 2 || !video.videoWidth) return
      if (!sizeChecked) {
        sizeChecked = true
        if (Math.min(video.videoWidth, video.videoHeight) < C.capture.minShortSide) {
          setSmallCamera(true)
        }
      }
      let result: FaceLandmarkerResult
      try {
        result = lm.detectForVideo(video, performance.now())
      } catch {
        if (++detectErrors >= C.maxDetectErrors) giveUpOnAuto()
        return
      }
      detectErrors = 0
      const faces = result.faceLandmarks ?? []
      if (faces.length > 0) lastFaceAt = time
      else if (time - lastFaceAt > C.troubleAfterMs) {
        giveUpOnAuto()
        return
      }
      let stats = null
      let box: FaceBox | null = null
      const lmk = faces.length === 1 ? (faces[0] ?? null) : null
      if (lmk) {
        box = faceBox(lmk)
        const vw = video.videoWidth
        const vh = video.videoHeight
        const sx = Math.max(0, box.x * vw)
        const sy = Math.max(0, box.y * vh)
        const sw = Math.max(1, Math.min(vw - sx, box.w * vw))
        const sh = Math.max(1, Math.min(vh - sy, box.h * vh))
        small.width = C.statsWidth
        small.height = Math.max(3, Math.round((C.statsWidth * sh) / sw))
        const ctx = small.getContext('2d', { willReadFrequently: true })
        if (ctx) {
          ctx.drawImage(video, sx, sy, sw, sh, 0, 0, small.width, small.height)
          stats = pixelStats(ctx.getImageData(0, 0, small.width, small.height))
        }
      }
      boxRef.current = box
      const ev = evaluateFrame(faces, stats)
      if (ev.ok) {
        badSince = null
        okSince ??= time
      } else if (okSince !== null) {
        badSince ??= time
        if (time - badSince > C.okGraceMs) okSince = badSince = null
      }
      const held = okSince === null ? 0 : time - okSince
      const next: Ui = {
        hint: ev.hint,
        checks: ev.checks,
        ok: ev.ok,
        progress: Math.floor(Math.min(1, held / C.stableMs) * 20) / 20,
      }
      const key = JSON.stringify(next)
      if (key !== lastKey) {
        lastKey = key
        setUi(next)
      }
      if (ev.ok && held >= C.stableMs) {
        stopped = true
        if (!finish(box)) {
          // Could not encode (e.g. camera too weak): start over, notice shown.
          okSince = badSince = null
          lastKey = ''
          stopped = false
          raf = requestAnimationFrame(tick)
        }
      }
    }
    raf = requestAnimationFrame(tick)
    return () => {
      stopped = true
      clearTimeout(troubleTimer)
      document.removeEventListener('visibilitychange', onVisibility)
      cancelAnimationFrame(raf)
    }
  }, [camera, engine, finish, attempt])

  /** Manual capture when automatic checks are unavailable: IMAGE mode on the
   * still if the landmarker did load, otherwise only the resolution check. */
  async function manualFallbackCapture() {
    const video = videoRef.current
    if (!video || !video.videoWidth) return
    setNotice(null)
    let face: FaceBox | null = null
    const lm = landmarkerRef.current
    const frame = document.createElement('canvas')
    frame.width = video.videoWidth
    frame.height = video.videoHeight
    frame.getContext('2d')?.drawImage(video, 0, 0)
    if (lm && !skipDetectRef.current) {
      try {
        await lm.setOptions({ runningMode: 'IMAGE' })
        const faces = lm.detect(frame).faceLandmarks ?? []
        const ev = evaluateFrame(faces, null)
        if (
          ev.hint === 'noFace' ||
          ev.hint === 'multipleFaces' ||
          ev.hint === 'tooFar' ||
          ev.hint === 'tooClose'
        ) {
          // Two rejections in a row: let the person through (centre crop).
          if (++rejectsRef.current < 2) {
            setNotice(t(`enroll.selfie.hint.${ev.hint}`))
            return
          }
        } else if (faces[0]) {
          face = faceBox(faces[0])
        }
      } catch {
        // Detection failed: fall through to the resolution-only check.
      }
    }
    finish(face, frame)
  }

  if (camera === 'denied' || camera === 'unavailable' || camera === 'stopped') {
    return (
      <div data-testid="selfie-camera-error" className="flex flex-col gap-3">
        <Banner
          variant="alert"
          title={t(
            camera === 'stopped'
              ? 'enroll.selfie.camera.stoppedTitle'
              : 'enroll.selfie.camera.title',
          )}
          description={t(
            camera === 'denied'
              ? 'enroll.selfie.camera.denied'
              : camera === 'stopped'
                ? 'enroll.selfie.camera.stopped'
                : 'enroll.selfie.camera.missing',
          )}
        />
        <Button
          variant="primary"
          icon={<CameraIcon size={20} />}
          onClick={() => setAttempt((n) => n + 1)}
          className="enroll-cta w-full"
        >
          {t('enroll.selfie.camera.retry')}
        </Button>
      </div>
    )
  }

  const auto = engine === 'ready'
  const fallback = engine === 'unavailable'
  const hintKey: string | null = !auto
    ? null
    : ui.hint
      ? `enroll.selfie.hint.${ui.hint}`
      : ui.ok
        ? 'enroll.selfie.hint.holdStill'
        : 'enroll.selfie.hint.noFace'
  const canTake = fallback ? camera === 'ready' : auto && ui.ok

  return (
    <div data-testid="selfie" className="flex flex-col gap-3">
      <div ref={frameRef} className="selfie-frame">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- live self-capture preview, no audio/captions apply */}
        <video ref={videoRef} autoPlay playsInline muted className="selfie-video" />
        <div
          className={`selfie-oval${auto && ui.ok ? ' selfie-oval--ok' : ''}`}
          aria-hidden="true"
        />
      </div>

      <div aria-live="polite" role="status" className="selfie-hint" data-testid="selfie-hint">
        {camera === 'starting' || engine === 'loading' ? (
          <span className="flex items-center gap-2">
            <Loader size="sm" />
            <Text as="span">{t('enroll.selfie.loading')}</Text>
          </span>
        ) : fallback ? (
          <Text>{t('enroll.selfie.manual')}</Text>
        ) : (
          <div className="flex w-full flex-col gap-1">
            {hintKey && <Text bold>{t(hintKey)}</Text>}
            {ui.progress > 0 && (
              <Meter
                label={t('enroll.selfie.countdown', {
                  seconds: String(Math.max(1, Math.ceil(((1 - ui.progress) * C.stableMs) / 1000))),
                })}
                value={ui.progress * 100}
                showValue={false}
              />
            )}
          </div>
        )}
      </div>

      {smallCamera && <Banner variant="alert">{t('enroll.selfie.note.small')}</Banner>}
      {notice && <Banner variant="error">{notice}</Banner>}

      {auto && (
        <ul className="flex flex-col gap-1" aria-label={t('enroll.selfie.checklist')}>
          {CHECKLIST.map(({ key, label }) => {
            const done = ui.checks[key]
            return (
              <li key={key} className="flex items-center gap-2" data-done={done}>
                {done ? (
                  <CheckCircleIcon size={18} weight="fill" className="shrink-0 text-kumo-success" />
                ) : (
                  <CircleIcon size={18} className="shrink-0 text-kumo-inactive" />
                )}
                <Text variant={done ? 'success' : 'secondary'}>{t(label)}</Text>
                <span className="sr-only">
                  {t(done ? 'enroll.selfie.check.done' : 'enroll.selfie.check.pending')}
                </span>
              </li>
            )
          })}
        </ul>
      )}

      <Text variant="secondary" size="sm">
        {t('enroll.selfie.privacy')}
      </Text>

      <StickyActionBar>
        <Button
          variant="primary"
          icon={<CameraIcon size={20} />}
          disabled={!canTake}
          onClick={() => {
            if (fallback) void manualFallbackCapture()
            else finish(boxRef.current)
          }}
          className="enroll-cta w-full"
        >
          {t('enroll.step3.capture')}
        </Button>
        {auto && troubled && (
          <Button
            variant="secondary"
            disabled={!ui.checks.oneFace}
            onClick={() => finish(boxRef.current)}
            className="enroll-cta mt-2 w-full"
          >
            {t('enroll.selfie.trouble')}
          </Button>
        )}
      </StickyActionBar>
    </div>
  )
}
